'use client';

import { useCallback } from 'react';
import { i18nConfig } from 'i18n-config';

const normalizeMcPathname = (pathname: string, locale: Locale) => {
  if (locale !== i18nConfig.defaultLocale) return pathname;

  const defaultLocalePrefix = `/${i18nConfig.defaultLocale}`;
  if (pathname === defaultLocalePrefix) return '/';

  return pathname.startsWith(`${defaultLocalePrefix}/`)
    ? pathname.slice(defaultLocalePrefix.length)
    : pathname;
};

const normalizeMcRelativeUrl = (url: string, locale: Locale) => {
  if (typeof window === 'undefined') return url;

  const parsedUrl = new URL(url, window.location.origin);
  return `${normalizeMcPathname(parsedUrl.pathname, locale)}${parsedUrl.search}${parsedUrl.hash}`;
};

/**
 * Native history helper for Mission Control pages.
 * Keeps default-locale URLs canonical and skips duplicate logical history writes.
 */
export const useMcHistory = (locale: Locale) => {
  const getCurrentUrl = useCallback(() => {
    if (typeof window === 'undefined') return '';

    return normalizeMcRelativeUrl(
      `${window.location.pathname}${window.location.search}${window.location.hash}`,
      locale,
    );
  }, [locale]);

  const getCurrentPathname = useCallback(() => {
    if (typeof window === 'undefined') return '';

    return normalizeMcPathname(window.location.pathname, locale);
  }, [locale]);

  const normalizeUrl = useCallback((url: string) => normalizeMcRelativeUrl(url, locale), [locale]);

  const writeHistory = useCallback((method: 'pushState' | 'replaceState', url: string, state?: unknown) => {
    if (typeof window === 'undefined') return false;

    const normalizedTargetUrl = normalizeUrl(url);
    const currentActualUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const currentNormalizedUrl = normalizeUrl(currentActualUrl);
    const nextState = typeof state === 'undefined' ? null : state;

    if (currentNormalizedUrl === normalizedTargetUrl) {
      if (currentActualUrl !== normalizedTargetUrl) {
        window.history.replaceState(nextState, '', normalizedTargetUrl);
      }
      return false;
    }

    window.history[method](nextState, '', normalizedTargetUrl);
    return true;
  }, [locale, normalizeUrl]);

  const push = useCallback((url: string, state?: unknown) => writeHistory('pushState', url, state), [writeHistory]);
  const replace = useCallback((url: string, state?: unknown) => writeHistory('replaceState', url, state), [writeHistory]);

  return {
    getCurrentPathname,
    getCurrentUrl,
    normalizeUrl,
    push,
    replace,
  };
};
